import React, { useState, useEffect } from 'react';
import {
  Bell, BellOff, Plus, Trash2, RefreshCw,
  CheckCircle, XCircle, Shield, Loader2, AlertCircle,
  FileText, ShoppingCart, Package, Boxes, ClipboardList, Fuel,
  User, Globe
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

const MODULE_FIELDS = [
  { key: 'po',          label: 'PO',           Icon: ShoppingCart  },
  { key: 'pr',          label: 'PR',           Icon: FileText      },
  { key: 'gr',          label: 'GR',           Icon: Package       },
  { key: 'inventory',   label: 'Inventory',    Icon: Boxes         },
  { key: 'itemRequest', label: 'Item Request', Icon: ClipboardList },
  { key: 'fuel',        label: 'Fuel',         Icon: Fuel          },
];

const NOTICE_FIELDS = [
  { key: 'ownNotice', label: 'Own Notice', Icon: User  },
  { key: 'allNotice', label: 'All Notice', Icon: Globe },
];

const DEFAULT_MODULES = { po: false, pr: false, gr: false, inventory: false, itemRequest: false, fuel: false };
const DEFAULT_NOTICES = { ownNotice: false, allNotice: false };

function ToggleBtn({ label, Icon, value, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
      border: `1.5px solid ${value ? '#16a34a44' : '#e2e8f0'}`,
      background: value ? '#f0fdf4' : '#f8fafc',
      color: value ? '#16a34a' : '#94a3b8',
      transition: 'all .15s', whiteSpace: 'nowrap',
    }}>
      <Icon size={12} />
      {label}
      {value ? <CheckCircle size={11} color="#16a34a" /> : <XCircle size={11} color="#cbd5e1" />}
    </button>
  );
}

function CellToggle({ value, onClick }) {
  return (
    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
      <button onClick={onClick} style={{
        width: 30, height: 30, borderRadius: 7, cursor: 'pointer',
        border: `1.5px solid ${value ? '#16a34a44' : '#e2e8f0'}`,
        background: value ? '#f0fdf4' : '#f8fafc',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .15s',
      }}>
        {value ? <CheckCircle size={13} color="#16a34a" /> : <XCircle size={13} color="#cbd5e1" />}
      </button>
    </td>
  );
}

const TH = ({ children, style = {} }) => (
  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap', ...style }}>{children}</th>
);

export default function NotificationConfig() {
  const [configs, setConfigs]                   = useState([]);
  const [positions, setPositions]               = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [posLoading, setPosLoading]             = useState(true);
  const [submitting, setSubmitting]             = useState(false);
  const [mounted, setMounted]                   = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [allowedNotif, setAllowedNotif]         = useState(true);
  const [modules, setModules]                   = useState({ ...DEFAULT_MODULES });
  const [notices, setNotices]                   = useState({ ...DEFAULT_NOTICES });

  useEffect(() => { fetchConfigs(); fetchPositions(); setTimeout(() => setMounted(true), 80); }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/Configuration_Notice`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConfigs(Array.isArray(data) ? data : [data]);
    } catch (err) { console.error(err); setConfigs([]); }
    finally { setLoading(false); }
  };

  const fetchPositions = async () => {
    setPosLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/UserLevels`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPositions((Array.isArray(data) ? data : [data]).filter(u => u.isActive === 1));
    } catch (err) { console.error(err); setPositions([]); }
    finally { setPosLoading(false); }
  };

  const handleAdd = async () => {
    if (!selectedPosition) { Swal.fire({ icon: 'warning', title: 'Select Position', text: 'Please select a position.', confirmButtonColor: '#1d4ed8' }); return; }
    if (configs.some(c => c.position?.toLowerCase() === selectedPosition.toLowerCase())) { Swal.fire({ icon: 'warning', title: 'Already Exists', text: `"${selectedPosition}" already has a config.`, confirmButtonColor: '#1d4ed8' }); return; }
    setSubmitting(true);
    try {
      const payload = { id: 0, position: selectedPosition, allowed_Notification: allowedNotif, ...modules, ...notices };
      const res = await fetch(`${API_BASE_URL}api/Configuration_Notice`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed to add config');
      setSelectedPosition(''); setAllowedNotif(true); setModules({ ...DEFAULT_MODULES }); setNotices({ ...DEFAULT_NOTICES });
      await fetchConfigs();
      Swal.fire({ icon: 'success', title: 'Added!', text: `Config for "${selectedPosition}" added.`, timer: 2000, showConfirmButton: false });
    } catch (err) { Swal.fire({ icon: 'error', title: 'Failed', text: err.message, confirmButtonColor: '#dc2626' }); }
    finally { setSubmitting(false); }
  };

  const handleToggleAllowed = async (config) => {
    const updated = { ...config, allowed_Notification: !config.allowed_Notification };
    try {
      const res = await fetch(`${API_BASE_URL}api/Configuration_Notice/${config.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) });
      if (!res.ok) throw new Error('Failed');
      setConfigs(prev => prev.map(c => c.id === config.id ? updated : c));
    } catch (err) { Swal.fire({ icon: 'error', title: 'Toggle Failed', text: err.message, confirmButtonColor: '#dc2626' }); }
  };

  const handleFieldToggle = async (config, key) => {
    const updated = { ...config, [key]: !config[key] };
    try {
      const res = await fetch(`${API_BASE_URL}api/Configuration_Notice/${config.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updated) });
      if (!res.ok) throw new Error('Failed');
      setConfigs(prev => prev.map(c => c.id === config.id ? updated : c));
    } catch (err) { Swal.fire({ icon: 'error', title: 'Update Failed', text: err.message, confirmButtonColor: '#dc2626' }); }
  };

  const handleDelete = async (config) => {
    const result = await Swal.fire({ icon: 'warning', title: 'Remove Config?', html: `Remove config for <strong>${config.position}</strong>?`, showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280', confirmButtonText: 'Yes, Remove' });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}api/Configuration_Notice/${config.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed');
      setConfigs(prev => prev.filter(c => c.id !== config.id));
      Swal.fire({ icon: 'success', title: 'Removed!', timer: 1800, showConfirmButton: false });
    } catch (err) { Swal.fire({ icon: 'error', title: 'Delete Failed', text: err.message, confirmButtonColor: '#dc2626' }); }
  };

  const availablePositions = positions.filter(p => !configs.some(c => c.position?.toLowerCase() === p.userLevelName?.toLowerCase()));
  const enabledCount  = configs.filter(c => c.allowed_Notification).length;
  const disabledCount = configs.filter(c => !c.allowed_Notification).length;

  return (
    <div style={{
      minHeight: '100vh', background: '#f8fafc', padding: '28px 20px',
      fontFamily: "'Segoe UI', sans-serif",
      opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(12px)',
      transition: 'opacity .35s ease, transform .35s ease',
    }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: '#1d4ed8', zIndex: 100 }} />

      <div style={{ maxWidth: 1500, margin: '0 auto' }}>

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bell size={19} color="#fff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#0f172a' }}>Notification Config</h1>
              <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Manage notification settings per position</p>
            </div>
          </div>
          <button onClick={() => { fetchConfigs(); fetchPositions(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 15px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 9, color: '#475569', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}>
            <RefreshCw size={13} color="#1d4ed8" /> Refresh
          </button>
        </div>

        {/* STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 18 }}>
          {[
            { label: 'Total Configured', val: configs.length, color: '#1d4ed8', bg: '#eff6ff', border: '#dbeafe' },
            { label: 'Notifications ON',  val: enabledCount,   color: '#16a34a', bg: '#f0fdf4', border: '#dcfce7' },
            { label: 'Notifications OFF', val: disabledCount,  color: '#dc2626', bg: '#fef2f2', border: '#fee2e2' },
          ].map(({ label, val, color, bg, border }) => (
            <div key={label} style={{ background: bg, border: `1px solid ${border}`, borderLeft: `4px solid ${color}`, borderRadius: 12, padding: '14px 20px' }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, color: color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
              <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: color }}>{val}</p>
            </div>
          ))}
        </div>

        {/* ADD FORM */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 13, padding: '18px 20px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 15 }}>
            <Plus size={14} color="#1d4ed8" />
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Add Notification Config</h2>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Position</label>
              {posLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9 }}>
                  <Loader2 size={13} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading...</span>
                </div>
              ) : (
                <select value={selectedPosition} onChange={e => setSelectedPosition(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 13px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 9, color: selectedPosition ? '#0f172a' : '#94a3b8', fontSize: 13, outline: 'none', fontFamily: 'inherit', cursor: 'pointer', appearance: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                  onBlur={e  => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="">— Select Position —</option>
                  {availablePositions.length === 0
                    ? <option disabled>All positions configured</option>
                    : availablePositions.map(p => <option key={p.id} value={p.userLevelName}>{p.userLevelName}</option>)
                  }
                </select>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>Allow Notifications</label>
              <button onClick={() => setAllowedNotif(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, background: allowedNotif ? '#f0fdf4' : '#fef2f2', color: allowedNotif ? '#16a34a' : '#dc2626', border: `1.5px solid ${allowedNotif ? '#bbf7d0' : '#fecaca'}`, transition: 'all .15s' }}>
                {allowedNotif ? <><CheckCircle size={14} /> Enabled</> : <><XCircle size={14} /> Disabled</>}
              </button>
            </div>

            <button onClick={handleAdd} disabled={submitting || !selectedPosition}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', background: submitting || !selectedPosition ? '#e2e8f0' : '#1d4ed8', color: submitting || !selectedPosition ? '#94a3b8' : '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: submitting || !selectedPosition ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}>
              {submitting ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Adding...</> : <><Plus size={13} /> Add Config</>}
            </button>
          </div>

          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 13 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Modules</label>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {MODULE_FIELDS.map(({ key, label, Icon }) => (
                <ToggleBtn key={key} label={label} Icon={Icon} value={modules[key]} onClick={() => setModules(p => ({ ...p, [key]: !p[key] }))} />
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 13, marginTop: 13 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Notice Scope</label>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {NOTICE_FIELDS.map(({ key, label, Icon }) => (
                <ToggleBtn key={key} label={label} Icon={Icon} value={notices[key]} onClick={() => setNotices(p => ({ ...p, [key]: !p[key] }))} />
              ))}
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 13, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Shield size={13} color="#1d4ed8" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Configured Positions</span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #dbeafe' }}>{configs.length}</span>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 0', gap: 10 }}>
              <Loader2 size={28} color="#dbeafe" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading...</span>
            </div>
          ) : configs.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 0', gap: 8 }}>
              <BellOff size={34} color="#cbd5e1" />
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#94a3b8' }}>No configurations yet</p>
              <p style={{ margin: 0, fontSize: 12, color: '#cbd5e1' }}>Add a position above to get started</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <TH>#</TH>
                    <TH style={{ textAlign: 'left' }}>Position</TH>
                    <TH>Notif</TH>
                    {MODULE_FIELDS.map(m => <TH key={m.key}>{m.label}</TH>)}
                    <TH style={{ borderLeft: '2px solid #e2e8f0' }}>Own Notice</TH>
                    <TH>All Notice</TH>
                    <TH>Actions</TH>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((config, idx) => (
                    <tr key={config.id}
                      style={{ borderBottom: idx < configs.length - 1 ? '1px solid #f1f5f9' : 'none', transition: 'background .12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '11px 12px', textAlign: 'center', fontSize: 12, color: '#cbd5e1', fontWeight: 600 }}>{idx + 1}</td>

                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: config.allowed_Notification ? '#1d4ed8' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {config.allowed_Notification ? <Bell size={13} color="#fff" /> : <BellOff size={13} color="#fff" />}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{config.position}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>ID: {config.id}</div>
                          </div>
                        </div>
                      </td>

                      <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                        <button onClick={() => handleToggleAllowed(config)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, border: `1.5px solid ${config.allowed_Notification ? '#bbf7d0' : '#fecaca'}`, background: config.allowed_Notification ? '#f0fdf4' : '#fef2f2', color: config.allowed_Notification ? '#16a34a' : '#dc2626', transition: 'all .15s', whiteSpace: 'nowrap' }}>
                          {config.allowed_Notification ? <><CheckCircle size={11} /> ON</> : <><XCircle size={11} /> OFF</>}
                        </button>
                      </td>

                      {MODULE_FIELDS.map(({ key }) => (
                        <CellToggle key={key} value={config[key]} onClick={() => handleFieldToggle(config, key)} />
                      ))}

                      <td style={{ padding: '10px 12px', textAlign: 'center', borderLeft: '2px solid #e2e8f0' }}>
                        <button onClick={() => handleFieldToggle(config, 'ownNotice')}
                          style={{ width: 30, height: 30, borderRadius: 7, cursor: 'pointer', border: `1.5px solid ${config.ownNotice ? '#16a34a44' : '#e2e8f0'}`, background: config.ownNotice ? '#f0fdf4' : '#f8fafc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                          {config.ownNotice ? <CheckCircle size={13} color="#16a34a" /> : <XCircle size={13} color="#cbd5e1" />}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <button onClick={() => handleFieldToggle(config, 'allNotice')}
                          style={{ width: 30, height: 30, borderRadius: 7, cursor: 'pointer', border: `1.5px solid ${config.allNotice ? '#16a34a44' : '#e2e8f0'}`, background: config.allNotice ? '#f0fdf4' : '#f8fafc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                          {config.allNotice ? <CheckCircle size={13} color="#16a34a" /> : <XCircle size={13} color="#cbd5e1" />}
                        </button>
                      </td>

                      <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                        <button onClick={() => handleDelete(config)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', transition: 'all .15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, padding: '9px 14px', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 9 }}>
          <AlertCircle size={12} color="#1d4ed8" />
          <span style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 500 }}>Click any toggle in the table to instantly save changes.</span>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        select option { background: #fff; color: #0f172a; }
      `}</style>
    </div>
  );
}